/**
 * MLReadinessLegacyWrapper — Thin React wrapper around the legacy ML Analysis card
 * ============================================================================
 *
 * Mirrors the DataQualityScorecard wrapper pattern: fetch ML readiness
 * results, then delegate rendering to public/js/mlAnalysis.js's
 * renderMLAnalysisCard() so the Journey Detail ML tab shows the same gauge,
 * dimension bars, summary, and sub-metric "?" info buttons that Kathleen
 * has seen since Sprint 6 (Task 814).
 *
 * Before PR #32, "View Results" on ML Readiness opened the legacy Control
 * Tower page (#/utils?job_id=…) which is where renderMLAnalysisCard renders.
 * PR #32 routed ML to ?tab=ml-readiness and mounted MLReadinessResults — a
 * pure-React component built for a different surface that did not match
 * what the user had been seeing for months. This wrapper restores parity.
 *
 * Sub-metric drawer: window.mountMLMetricDrawer (set by main.tsx via
 * MLMetricDrawerMount.tsx) handles the right-side drawer. Click delegation
 * for .ml-metric-info-btn → ml-metric-click event is installed at module
 * load by mlAnalysis.js itself (line 64), so we don't need to bind it here.
 *
 * @created 2026-05-12 (DC-1549 redo follow-up)
 */

import React, { useEffect, useRef, useState } from 'react';

interface MLSufficiencyVolume { score: number; sampleCount?: number; status: string; }
interface MLSufficiencyCoverage { score: number; completeness?: number; status: string; }
interface MLSufficiencyBalance {
  score: number;
  balanceRatio?: number;
  status: string;
  classDistribution?: Record<string, number>;
}

interface MLSufficiency {
  overall: number;
  volume: MLSufficiencyVolume;
  coverage: MLSufficiencyCoverage;
  balance: MLSufficiencyBalance;
  recommendations?: string[];
}

interface MLBias {
  overall: number;
  riskLevel?: 'low' | 'medium' | 'high';
  demographics?: Record<string, unknown>;
  outcomeBias?: { detected?: boolean; maxRiskRatio?: number };
  featureBias?: { proxyVariablesDetected?: string[] };
}

interface MLFeatureEngineering {
  overall: number;
  issues: Array<{ severity: 'critical' | 'warning' | 'info'; message?: string }>;
  dataTypes?: Record<string, unknown>;
}

interface MLReadinessBundle {
  version?: string;
  jobId?: string;
  datasetId?: string;
  timestamp?: string;
  overall?: number;
  overallReadiness?: number;
  summary?: string;
  sufficiency: MLSufficiency;
  bias: MLBias;
  featureEngineering: MLFeatureEngineering;
  recommendations?: unknown[];
}

interface MLAnalysisAPI {
  renderMLAnalysisCard: (bundle: MLReadinessBundle) => string;
  renderMLAnalysisPlaceholder: () => string;
  exportMLAnalysisReport?: (jobId: string) => Promise<void> | void;
}

interface MLAnalysisModalAPI {
  openMLAnalysisModal: (bundle: MLReadinessBundle, jobId: string) => void;
  closeMLAnalysisModal: () => void;
}

interface MLReadinessLegacyWrapperProps {
  jobId: string;
  onError?: (error: string) => void;
  onRetry?: () => void;
}

// ============================================================================
// PHASE 3 (Deep Analysis) TYPES — mirrors MLReadinessResults.tsx so the same
// /api/ml-readiness/{jobId}/{phase3-summary,class-separability,…} endpoints
// can populate the Deep Analysis section below the legacy card.
// ============================================================================

interface Phase3Summary {
  sampleSizeAdequate?: boolean;
  factor50Score?: number;
  powerAnalysisScore?: number;
}

interface ClassSeparabilityData {
  overallScore?: number;
  fisherRatioAvg?: number;
  rValueScore?: number;
}

interface DiscriminationIndexData {
  overallScore?: number;
  maxCDD?: number;
  riskLevel?: 'low' | 'medium' | 'high' | 'critical';
  significantDisparities?: Array<{ protected_attribute: string }>;
}

interface DataPointImpactData {
  highInfluenceCount?: number;
}

interface MislabelData {
  cohensKappa?: number;
  mislabels?: Array<{ row_index: number }>;
}

function getKappaInterpretation(kappa: number): string {
  if (kappa >= 0.81) return 'Almost Perfect';
  if (kappa >= 0.61) return 'Substantial';
  if (kappa >= 0.41) return 'Moderate';
  if (kappa >= 0.21) return 'Fair';
  return 'Poor';
}

export const MLReadinessLegacyWrapper: React.FC<MLReadinessLegacyWrapperProps> = ({
  jobId,
  onError,
  onRetry,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bundle, setBundle] = useState<MLReadinessBundle | null>(null);

  // Phase 3 (Deep Analysis) state
  const [phase3Summary, setPhase3Summary] = useState<Phase3Summary | null>(null);
  const [classSeparability, setClassSeparability] = useState<ClassSeparabilityData | null>(null);
  const [discriminationIndex, setDiscriminationIndex] = useState<DiscriminationIndexData | null>(null);
  const [dataPointImpact, setDataPointImpact] = useState<DataPointImpactData | null>(null);
  const [mislabelData, setMislabelData] = useState<MislabelData | null>(null);

  const hasPhase3Data = phase3Summary || classSeparability || discriminationIndex || dataPointImpact || mislabelData;

  useEffect(() => {
    let cancelled = false;

    const fetchResults = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`/api/ml-readiness/${jobId}/results`);
        if (!response.ok) {
          throw new Error(`Failed to fetch ML readiness results: ${response.statusText}`);
        }
        const data = await response.json();

        if (cancelled) return;

        if (data.status === 'failed') {
          throw new Error(data.error || 'ML readiness analysis failed');
        }

        if (data.status === 'processing') {
          window.setTimeout(fetchResults, 2000);
          return;
        }

        const resolved: MLReadinessBundle = data.bundle ?? data;
        setBundle(resolved);
        setLoading(false);
      } catch (err: unknown) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Failed to fetch ML readiness results';
        console.error('[MLReadinessLegacyWrapper] fetch failed:', err);
        setError(msg);
        setLoading(false);
        onError?.(msg);
      }
    };

    fetchResults();
    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onError stability not required
  }, [jobId]);

  // Fetch Phase 3 deep-analysis data in parallel with the main bundle.
  // Each endpoint returns success=false (or 4xx) when deep analysis wasn't
  // run; in that case we leave the state null and the section renders nothing.
  useEffect(() => {
    let cancelled = false;

    const safeJson = async (url: string): Promise<{ ok: boolean; data: unknown }> => {
      try {
        const res = await fetch(url);
        if (!res.ok) return { ok: false, data: null };
        const json = await res.json();
        return { ok: true, data: json };
      } catch {
        return { ok: false, data: null };
      }
    };

    Promise.all([
      safeJson(`/api/ml-readiness/${jobId}/phase3-summary`),
      safeJson(`/api/ml-readiness/${jobId}/class-separability`),
      safeJson(`/api/ml-readiness/${jobId}/discrimination-index`),
      safeJson(`/api/ml-readiness/${jobId}/data-point-impact`),
      safeJson(`/api/ml-readiness/${jobId}/mislabels`),
    ]).then(([summary, sep, disc, impact, mislabel]) => {
      if (cancelled) return;
      type SummaryShape = { success?: boolean; summary?: Phase3Summary };
      type SepShape = { success?: boolean; overallScore?: number; fisherRatioAvg?: number; rValueScore?: number };
      type DiscShape = { success?: boolean; overallScore?: number; maxCDD?: number; riskLevel?: 'low' | 'medium' | 'high' | 'critical'; significantDisparities?: Array<{ protected_attribute: string }> };
      type ImpactShape = { success?: boolean; highInfluenceCount?: number };
      type MisShape = { success?: boolean; cohensKappa?: number; mislabels?: Array<{ row_index: number }> };

      const s = summary.data as SummaryShape;
      if (summary.ok && s?.success && s.summary) setPhase3Summary(s.summary);

      const sp = sep.data as SepShape;
      if (sep.ok && sp?.success) {
        setClassSeparability({ overallScore: sp.overallScore, fisherRatioAvg: sp.fisherRatioAvg, rValueScore: sp.rValueScore });
      }

      const d = disc.data as DiscShape;
      if (disc.ok && d?.success) {
        setDiscriminationIndex({ overallScore: d.overallScore, maxCDD: d.maxCDD, riskLevel: d.riskLevel, significantDisparities: d.significantDisparities });
      }

      const im = impact.data as ImpactShape;
      if (impact.ok && im?.success) setDataPointImpact({ highInfluenceCount: im.highInfluenceCount });

      const m = mislabel.data as MisShape;
      if (mislabel.ok && m?.success) setMislabelData({ cohensKappa: m.cohensKappa, mislabels: m.mislabels });
    });

    return () => { cancelled = true; };
  }, [jobId]);

  // Render the legacy ML card once the bundle arrives. mlAnalysis.js is
  // loaded as type="module" by main.tsx, so it may not be ready on the
  // first paint. Poll briefly (≤2s) for window.MLAnalysis before falling
  // back to a plain summary. Once rendered, wire the card's "View Details"
  // and "Export Report" buttons — those handlers normally live in
  // controlTower.js which the SPA doesn't load.
  useEffect(() => {
    if (!bundle || !containerRef.current) return;
    let cancelled = false;
    let elapsed = 0;
    const POLL_MS = 50;
    const MAX_MS = 2000;

    const wireCardButtons = (root: HTMLDivElement) => {
      const viewDetailsBtn = root.querySelector<HTMLButtonElement>('[data-action="view-ml-analysis-details"]');
      if (viewDetailsBtn) {
        viewDetailsBtn.addEventListener('click', () => {
          const modal = (window as unknown as { MLAnalysisModal?: MLAnalysisModalAPI }).MLAnalysisModal;
          if (modal && typeof modal.openMLAnalysisModal === 'function') {
            modal.openMLAnalysisModal(bundle, jobId);
          } else {
            console.warn('[MLReadinessLegacyWrapper] window.MLAnalysisModal not ready; click ignored');
          }
        });
      }
      const exportBtn = root.querySelector<HTMLButtonElement>('[data-action="export-ml-analysis"]');
      if (exportBtn) {
        exportBtn.addEventListener('click', async () => {
          const ml = (window as unknown as { MLAnalysis?: MLAnalysisAPI }).MLAnalysis;
          if (ml && typeof ml.exportMLAnalysisReport === 'function') {
            await ml.exportMLAnalysisReport(jobId);
          } else {
            console.warn('[MLReadinessLegacyWrapper] window.MLAnalysis.exportMLAnalysisReport not ready; click ignored');
          }
        });
      }
    };

    const tryRender = () => {
      if (cancelled || !containerRef.current) return;
      const ml = (window as unknown as { MLAnalysis?: MLAnalysisAPI }).MLAnalysis;
      if (ml && typeof ml.renderMLAnalysisCard === 'function') {
        containerRef.current.innerHTML = ml.renderMLAnalysisCard(bundle);
        wireCardButtons(containerRef.current);
        return;
      }
      if (elapsed >= MAX_MS) {
        console.warn('[MLReadinessLegacyWrapper] window.MLAnalysis not loaded after 2s; falling back to plain summary');
        const overall = Math.round(bundle.overall ?? bundle.overallReadiness ?? 0);
        const summary = bundle.summary ?? '';
        containerRef.current.innerHTML = `
          <div class="ml-analysis-placeholder">
            <h4>ML Readiness — ${overall}/100</h4>
            <p class="muted">${summary}</p>
          </div>
        `;
        return;
      }
      elapsed += POLL_MS;
      window.setTimeout(tryRender, POLL_MS);
    };

    tryRender();
    return () => {
      cancelled = true;
      // Close the modal if it's still open from this card — prevents a
      // stale modal hanging around after the user switches tabs.
      const modal = (window as unknown as { MLAnalysisModal?: MLAnalysisModalAPI }).MLAnalysisModal;
      if (modal && typeof modal.closeMLAnalysisModal === 'function') {
        modal.closeMLAnalysisModal();
      }
    };
  }, [bundle, jobId]);

  // Mount the React sub-metric drawer for this job (installs the
  // window-level ml-metric-click listener via MLMetricDrawerMount).
  useEffect(() => {
    const w = window as unknown as {
      mountMLMetricDrawer?: (id: string) => void;
      unmountMLMetricDrawer?: () => void;
    };
    if (jobId && typeof w.mountMLMetricDrawer === 'function') {
      w.mountMLMetricDrawer(jobId);
    }
    return () => {
      if (typeof w.unmountMLMetricDrawer === 'function') {
        w.unmountMLMetricDrawer();
      }
    };
  }, [jobId]);

  if (loading) {
    return (
      <div className="ml-analysis-card-wrapper">
        <div className="ml-analysis-placeholder">
          <h4>Loading ML readiness results…</h4>
          <p className="muted">Job {jobId}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ml-analysis-card-wrapper">
        <div className="ml-analysis-placeholder">
          <h4>Failed to load ML readiness</h4>
          <p className="muted">{error}</p>
          {onRetry && (
            <button type="button" className="ai-btn" onClick={onRetry}>Retry</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div ref={containerRef} className="ml-analysis-card-wrapper" data-job-id={jobId} />
      {hasPhase3Data && (
        <section className="ml-deep-analysis-section" aria-label="Deep ML analysis results">
          <h3 className="ml-deep-analysis-title">Deep Analysis Results</h3>

          {phase3Summary && (phase3Summary.factor50Score != null || phase3Summary.powerAnalysisScore != null) && (
            <div className="ml-deep-card">
              <header className="ml-deep-card-header">
                <h4>Sample Size Adequacy</h4>
                {phase3Summary.sampleSizeAdequate != null && (
                  <span className={`ml-deep-badge ${phase3Summary.sampleSizeAdequate ? 'badge-good' : 'badge-warn'}`}>
                    {phase3Summary.sampleSizeAdequate ? 'Adequate' : 'Insufficient'}
                  </span>
                )}
              </header>
              <div className="ml-deep-metrics">
                {phase3Summary.factor50Score != null && (
                  <div className="ml-deep-metric">
                    <span className="ml-deep-metric-label">Factor-50 Rule</span>
                    <span className={`ml-deep-metric-value ${phase3Summary.factor50Score >= 50 ? 'good' : 'warn'}`}>
                      {phase3Summary.factor50Score.toFixed(1)}:1
                    </span>
                    <span className="ml-deep-metric-hint">≥50:1 recommended</span>
                  </div>
                )}
                {phase3Summary.powerAnalysisScore != null && (
                  <div className="ml-deep-metric">
                    <span className="ml-deep-metric-label">Statistical Power</span>
                    <span className={`ml-deep-metric-value ${phase3Summary.powerAnalysisScore >= 80 ? 'good' : 'warn'}`}>
                      {phase3Summary.powerAnalysisScore.toFixed(0)}%
                    </span>
                    <span className="ml-deep-metric-hint">≥80% recommended</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {classSeparability && (
            <div className="ml-deep-card">
              <header className="ml-deep-card-header">
                <h4>Class Separability</h4>
                {classSeparability.overallScore != null && (
                  <span className={`ml-deep-badge ${classSeparability.overallScore >= 70 ? 'badge-good' : classSeparability.overallScore >= 50 ? 'badge-warn' : 'badge-bad'}`}>
                    {classSeparability.overallScore >= 70 ? 'Well Separated' : classSeparability.overallScore >= 50 ? 'Moderate' : 'Low Separability'}
                  </span>
                )}
              </header>
              <div className="ml-deep-metrics">
                {classSeparability.rValueScore != null && (
                  <div className="ml-deep-metric">
                    <span className="ml-deep-metric-label">R-Value Score</span>
                    <span className={`ml-deep-metric-value ${classSeparability.rValueScore >= 0.7 ? 'good' : 'warn'}`}>
                      {classSeparability.rValueScore.toFixed(2)}
                    </span>
                    <span className="ml-deep-metric-hint">Measures class distinctness</span>
                  </div>
                )}
                {classSeparability.fisherRatioAvg != null && (
                  <div className="ml-deep-metric">
                    <span className="ml-deep-metric-label">Fisher Ratio (avg)</span>
                    <span className={`ml-deep-metric-value ${classSeparability.fisherRatioAvg >= 1.0 ? 'good' : 'warn'}`}>
                      {classSeparability.fisherRatioAvg.toFixed(2)}
                    </span>
                    <span className="ml-deep-metric-hint">Higher = better separation</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {mislabelData && (
            <div className="ml-deep-card">
              <header className="ml-deep-card-header">
                <h4>Mislabel Detection</h4>
                {mislabelData.cohensKappa != null && (
                  <span className={`ml-deep-badge ${mislabelData.cohensKappa >= 0.8 ? 'badge-good' : mislabelData.cohensKappa >= 0.6 ? 'badge-warn' : 'badge-bad'}`}>
                    {getKappaInterpretation(mislabelData.cohensKappa)}
                  </span>
                )}
              </header>
              <div className="ml-deep-metrics">
                {mislabelData.cohensKappa != null && (
                  <div className="ml-deep-metric">
                    <span className="ml-deep-metric-label">Cohen&apos;s Kappa</span>
                    <span className={`ml-deep-metric-value ${mislabelData.cohensKappa >= 0.8 ? 'good' : 'warn'}`}>
                      {mislabelData.cohensKappa.toFixed(3)}
                    </span>
                    <span className="ml-deep-metric-hint">Label agreement score</span>
                  </div>
                )}
                {mislabelData.mislabels && (
                  <div className="ml-deep-metric">
                    <span className="ml-deep-metric-label">Suspected Mislabels</span>
                    <span className={`ml-deep-metric-value ${mislabelData.mislabels.length === 0 ? 'good' : 'warn'}`}>
                      {mislabelData.mislabels.length}
                    </span>
                    <span className="ml-deep-metric-hint">Rows to review</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {discriminationIndex && (
            <div className="ml-deep-card">
              <header className="ml-deep-card-header">
                <h4>Discrimination Index (Bias)</h4>
                {discriminationIndex.riskLevel && (
                  <span className={`ml-deep-badge ${discriminationIndex.riskLevel === 'low' ? 'badge-good' : discriminationIndex.riskLevel === 'medium' ? 'badge-warn' : 'badge-bad'}`}>
                    {discriminationIndex.riskLevel.charAt(0).toUpperCase() + discriminationIndex.riskLevel.slice(1)} Risk
                  </span>
                )}
              </header>
              <div className="ml-deep-metrics">
                {discriminationIndex.maxCDD != null && (
                  <div className="ml-deep-metric">
                    <span className="ml-deep-metric-label">Max CDD</span>
                    <span className={`ml-deep-metric-value ${discriminationIndex.maxCDD <= 0.1 ? 'good' : 'warn'}`}>
                      {discriminationIndex.maxCDD.toFixed(3)}
                    </span>
                    <span className="ml-deep-metric-hint">Conditional Demographic Disparity</span>
                  </div>
                )}
                {discriminationIndex.overallScore != null && (
                  <div className="ml-deep-metric">
                    <span className="ml-deep-metric-label">Fairness Score</span>
                    <span className={`ml-deep-metric-value ${discriminationIndex.overallScore >= 80 ? 'good' : 'warn'}`}>
                      {discriminationIndex.overallScore.toFixed(0)}
                    </span>
                    <span className="ml-deep-metric-hint">Higher = fairer model</span>
                  </div>
                )}
              </div>
              {discriminationIndex.significantDisparities && discriminationIndex.significantDisparities.length > 0 && (
                <div className="ml-deep-warning">
                  Significant disparities in: {discriminationIndex.significantDisparities.map((d) => d.protected_attribute).join(', ')}
                </div>
              )}
            </div>
          )}

          {dataPointImpact && (
            <div className="ml-deep-card">
              <header className="ml-deep-card-header">
                <h4>Data Point Impact</h4>
                {dataPointImpact.highInfluenceCount != null && (
                  <span className={`ml-deep-badge ${dataPointImpact.highInfluenceCount === 0 ? 'badge-good' : dataPointImpact.highInfluenceCount <= 5 ? 'badge-warn' : 'badge-bad'}`}>
                    {dataPointImpact.highInfluenceCount} high-influence points
                  </span>
                )}
              </header>
              <div className="ml-deep-metrics">
                <div className="ml-deep-metric">
                  <span className="ml-deep-metric-label">High Influence</span>
                  <span className={`ml-deep-metric-value ${(dataPointImpact.highInfluenceCount || 0) === 0 ? 'good' : 'warn'}`}>
                    {dataPointImpact.highInfluenceCount || 0}
                  </span>
                  <span className="ml-deep-metric-hint">Points with outsized impact</span>
                </div>
              </div>
            </div>
          )}
        </section>
      )}
    </>
  );
};

export default MLReadinessLegacyWrapper;
